import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { ArrowLeft, Play, MessagesSquare, Plus, Upload } from 'lucide-react';
import { useBrand } from '../../context/BrandContext';
import translationLabAPI from '../../services/translationLabService';
import { highlightSegments, missingSegmentValues, unionSegments } from '../../utils/placeholderHighlight';
import { FORMAT_EXAMPLE, countBlocks } from '../../utils/transcriptText';
import TranslationLabFeedbackControl from './TranslationLabFeedbackControl';

const RUN_STATUS_VARIANT = {
  completed: 'success',
  partial: 'warning',
  failed: 'destructive',
  running: 'secondary',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function runLabel(run) {
  if (!run.agent_id) return `run #${run.id}`;
  return run.agent_version ? `${run.agent_id}@${run.agent_version}` : run.agent_id;
}

// DetectionNote shows the two language verdicts bi-pepper returns on the unknown-source path: the
// language the translating agent worked from (authoritative — the translation was built from it)
// and its own independent lingua classification of the same text. They can disagree, and that is
// exactly what this line is for, so a disagreement is coloured rather than buried.
//
// Nothing is rendered on the fixed-pair path, where bi-pepper runs no detection at all.
function DetectionNote({ result }) {
  if (!result.detected_language) return null;

  const agrees = result.detected_language === result.source_language;
  const confidence = typeof result.detection_confidence === 'number'
    ? ` ${Math.round(result.detection_confidence * 100)}%`
    : '';

  return (
    <p className={`text-[11px] mt-0.5 ${agrees ? 'text-ink-mute' : 'text-brand-yellow'}`}>
      from {result.source_language || '—'} · detected {result.detected_language}{confidence}
    </p>
  );
}

// HistoryDialog shows what the agent actually received for one case: the context turns, then the
// message that was translated.
function HistoryDialog({ testCase, onOpenChange }) {
  return (
    <Dialog open={Boolean(testCase)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Conversation context</DialogTitle>
        </DialogHeader>
        {testCase && (
          <div className="space-y-2">
            {testCase.history.map((turn, i) => (
              <div key={i} className="rounded-lg border border-white/10 bg-surface-2 px-3 py-2">
                <span className="text-[10px] font-bold uppercase text-ink-mute">{turn.role}</span>
                <p className="text-sm text-ink-dim">{turn.content}</p>
              </div>
            ))}
            <div className="rounded-lg border border-cta/30 bg-cta/10 px-3 py-2">
              <span className="text-[10px] font-bold uppercase text-brand-green">
                translated{testCase.source_role ? ` · ${testCase.source_role}` : ''}
              </span>
              <p className="text-sm text-ink">{testCase.source_text}</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Mirrors the set cap the server enforces, so the dialog can warn before the round trip. The server
// stays the authority — same hint-only contract as countBlocks.
const MAX_CASES_PER_SET = 100;

// AddCasesDialog appends conversations to the existing set: the same paste-or-upload input as set
// creation, parsed by the same server-side rules. Growing a set this way keeps its run history —
// re-creating it would throw those runs away.
function AddCasesDialog({ open, onOpenChange, setId, existingCount, onAdded }) {
  const [rawText, setRawText] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const reset = () => {
    setRawText('');
    setError('');
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      setRawText(await file.text());
    } catch {
      setError('Could not read that file');
    } finally {
      e.target.value = ''; // allow re-picking the same file
    }
  };

  const handleAdd = async () => {
    if (!rawText.trim() || adding) return;
    setAdding(true);
    setError('');
    try {
      await translationLabAPI.addCases(setId, { raw_text: rawText });
      reset();
      onOpenChange(false);
      await onAdded();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add conversations');
    } finally {
      setAdding(false);
    }
  };

  const blocks = countBlocks(rawText);
  const total = existingCount + blocks;
  const overCapacity = blocks > 0 && total > MAX_CASES_PER_SET;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add conversations</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-ink-dim">
                Conversations <span className="text-ink-mute">(appended after the existing {existingCount})</span>
              </label>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-3.5 w-3.5" />Upload .txt
              </Button>
              <input ref={fileInputRef} type="file" accept=".txt,text/plain" className="hidden" onChange={handleFile} />
            </div>
            <Textarea
              rows={12}
              className="mt-1 font-mono text-xs"
              placeholder={FORMAT_EXAMPLE}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
            />
            <p className="text-[11px] text-ink-mute mt-1.5 leading-relaxed">
              A blank line separates conversations. In each conversation the <strong className="text-ink-dim">last line
              is translated and rated</strong>; the lines above it are sent as context only. Multi-line conversations
              need a <code className="text-ink-dim">user:</code> or <code className="text-ink-dim">assistant:</code> prefix
              on every line; a single-line conversation needs none.
            </p>
            {rawText.trim() && (
              <p className={`text-[11px] mt-1 ${overCapacity ? 'text-warning' : 'text-ink-dim'}`}>
                {blocks} conversation{blocks === 1 ? '' : 's'} detected
                {overCapacity
                  ? ` — the set would reach ${total}, over the limit of ${MAX_CASES_PER_SET}`
                  : ` · set would have ${total} of ${MAX_CASES_PER_SET}`}
              </p>
            )}
          </div>

          {error && (
            <Alert variant="error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleAdd} disabled={!rawText.trim() || adding || overCapacity} className="gap-1.5">
            {adding ? <Spinner size="sm" /> : <Plus className="h-4 w-4" />}Add conversations
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// RunControls offers the two ways bi-pepper lets you pick an agent: a language pair (it resolves
// the pair's default agent) or one specific agent. The second is what makes two agents on the same
// pair comparable, so it is one click away rather than hidden.
function RunControls({ agents, lastRun, running, onRun }) {
  const [byAgent, setByAgent] = useState(false);
  const [agentID, setAgentID] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('');
  // Guards the one-time default: after that the selects belong to the user, so a re-render must not
  // pull them back to the last run's pair.
  const pairsInitialised = useRef(false);

  // Only pairs an agent actually covers may be offered: the two selects are not independent, or you
  // could pick de -> es and get a 400 back from bi-pepper for a combination it never advertised.
  const pairs = useMemo(() => {
    const bySource = new Map();
    agents.forEach((a) => {
      if (!a.source_language || !a.target_language) return;
      if (!bySource.has(a.source_language)) bySource.set(a.source_language, new Set());
      bySource.get(a.source_language).add(a.target_language);
    });
    return bySource;
  }, [agents]);

  const sources = useMemo(() => [...pairs.keys()].sort(), [pairs]);
  const targets = useMemo(
    () => [...(pairs.get(sourceLanguage) || [])].sort(),
    [pairs, sourceLanguage],
  );

  // Default to the previous run's direction — the common case is re-running the same set with a
  // different agent over the same pair — but only if that pair still exists.
  useEffect(() => {
    if (pairsInitialised.current || pairs.size === 0) return;
    pairsInitialised.current = true;

    if (lastRun && pairs.get(lastRun.source_language)?.has(lastRun.target_language)) {
      setSourceLanguage(lastRun.source_language);
      setTargetLanguage(lastRun.target_language);
      return;
    }
    const firstSource = [...pairs.keys()].sort()[0];
    setSourceLanguage(firstSource);
    setTargetLanguage([...pairs.get(firstSource)].sort()[0]);
  }, [pairs, lastRun]);

  // Changing the source can invalidate the target, so move it to one this source does cover.
  const handleSourceChange = (nextSource) => {
    setSourceLanguage(nextSource);
    const allowed = pairs.get(nextSource);
    if (!allowed?.has(targetLanguage)) {
      setTargetLanguage(allowed ? [...allowed].sort()[0] : '');
    }
  };

  const canRun = byAgent ? Boolean(agentID) : Boolean(sourceLanguage && targetLanguage);

  const handleRun = () => {
    if (!canRun) return;
    onRun(byAgent
      ? { agent_id: agentID }
      : { source_language: sourceLanguage, target_language: targetLanguage });
  };

  return (
    <div className="flex items-center gap-2 flex-wrap justify-end">
      <div className="flex rounded-lg border border-white/10 overflow-hidden">
        <button
          type="button"
          onClick={() => setByAgent(false)}
          className={`px-2.5 py-1.5 text-xs font-bold transition-colors ${!byAgent ? 'bg-surface-3 text-ink' : 'text-ink-mute hover:text-ink'}`}
        >
          Language pair
        </button>
        <button
          type="button"
          onClick={() => setByAgent(true)}
          className={`px-2.5 py-1.5 text-xs font-bold transition-colors ${byAgent ? 'bg-surface-3 text-ink' : 'text-ink-mute hover:text-ink'}`}
        >
          Specific agent
        </button>
      </div>

      {byAgent ? (
        <Select value={agentID} onValueChange={setAgentID}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Pick an agent" />
          </SelectTrigger>
          <SelectContent>
            {agents.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.id} · v{a.version}{a.default ? ' (default)' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <>
          <Select value={sourceLanguage} onValueChange={handleSourceChange}>
            <SelectTrigger className="w-32"><SelectValue placeholder="From" /></SelectTrigger>
            <SelectContent>
              {sources.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={targetLanguage} onValueChange={setTargetLanguage} disabled={targets.length === 0}>
            <SelectTrigger className="w-32"><SelectValue placeholder="To" /></SelectTrigger>
            <SelectContent>
              {targets.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </>
      )}

      <Button onClick={handleRun} disabled={!canRun || running} className="gap-1.5">
        {running ? <Spinner size="sm" /> : <Play className="h-4 w-4" />}
        {lastRun ? 'Re-run' : 'Run'}
      </Button>
    </div>
  );
}

const TranslationLabSetPage = () => {
  const { setId } = useParams();
  const navigate = useNavigate();
  const { activeBrand } = useBrand();

  const [detail, setDetail] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [historyCase, setHistoryCase] = useState(null);
  const [hiddenRunIds, setHiddenRunIds] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  // Identifies the most recent load, so responses that arrive out of order can be dropped.
  const latestLoadID = useRef(0);
  // A run costs up to a hundred LLM calls, so it gets a synchronous guard rather than relying on
  // the button being disabled — that only takes effect once React has re-rendered.
  const runInFlight = useRef(false);

  const load = useCallback(async () => {
    // Loads overlap when you switch sets or brands faster than a response comes back, and the older
    // one can land last — putting another set's rows under this URL. Only the newest may write.
    const requestID = ++latestLoadID.current;
    setLoading(true);
    setError('');
    try {
      const [setResp, agentsResp] = await Promise.all([
        translationLabAPI.getSet(setId),
        translationLabAPI.listAgents(),
      ]);
      if (requestID !== latestLoadID.current) return;

      setDetail(setResp.data);
      setAgents(agentsResp.data.agents || []);

      // With more than two runs the table gets unreadable, so start on the two most recent and let
      // the chips bring the others back.
      const runs = setResp.data.runs || [];
      setHiddenRunIds(runs.length > 2 ? runs.slice(0, runs.length - 2).map((r) => r.id) : []);
    } catch (err) {
      if (requestID !== latestLoadID.current) return;
      // Drop what is on screen too. Now that a reload keeps the page mounted, a failed one would
      // otherwise leave the previous set rendered under an error banner — most visibly after a
      // brand switch, where the set belongs to the brand you just left.
      setDetail(null);
      setError(err.response?.data?.error || 'Failed to load the test set');
    } finally {
      if (requestID === latestLoadID.current) setLoading(false);
    }
  }, [setId]);

  useEffect(() => { load(); }, [load, activeBrand]);

  // Which set and brand the page is showing right now. A run takes minutes, and opening another set
  // while you wait is the natural thing to do — but this component is reused across :setId, so it
  // never unmounts and a finished run would otherwise refresh or error a page it no longer belongs
  // to, using the load it captured for the set you left.
  const scope = useRef({ setId, activeBrand });
  useEffect(() => { scope.current = { setId, activeBrand }; }, [setId, activeBrand]);

  const handleRun = async (body) => {
    if (runInFlight.current) return;
    const ranFor = { setId, activeBrand };
    const stillShowing = () => scope.current.setId === ranFor.setId
      && scope.current.activeBrand === ranFor.activeBrand;

    runInFlight.current = true;
    setRunning(true);
    setError('');
    try {
      await translationLabAPI.runSet(ranFor.setId, body);
      if (stillShowing()) await load();
    } catch (err) {
      if (stillShowing()) setError(err.response?.data?.error || 'The run failed');
    } finally {
      // Cleared unconditionally: these belong to this component, which is still mounted either way.
      runInFlight.current = false;
      setRunning(false);
    }
  };

  // Keep a saved rating on screen without refetching the whole set.
  const handleFeedbackSaved = (resultId, feedback) => {
    setDetail((prev) => prev && ({
      ...prev,
      runs: prev.runs.map((run) => ({
        ...run,
        results: run.results.map((r) => (r.id === resultId ? { ...r, feedback } : r)),
      })),
    }));
  };

  // Only the first load takes over the page. A reload — after a run, or a brand switch — leaves it
  // mounted: replacing it with a spinner would unmount RunControls, and remounting resets its mode
  // back to language-pair, so the agent you just picked silently becomes the pair's default and the
  // next Run repeats a run you already paid for. The Run button's own spinner covers the wait.
  if (loading && !detail) {
    return <div className="flex justify-center py-16"><Spinner size="lg" /></div>;
  }
  if (!detail) {
    return (
      <div className="max-w-[1100px] mx-auto py-6">
        <Alert variant="error"><AlertDescription>{error || 'Test set not found'}</AlertDescription></Alert>
        <Button variant="outline" className="mt-4 gap-1.5" onClick={() => navigate('/translation-lab')}>
          <ArrowLeft className="h-4 w-4" />Back to sets
        </Button>
      </div>
    );
  }

  const { set, cases, runs } = detail;
  const visibleRuns = runs.filter((r) => !hiddenRunIds.includes(r.id));
  const lastRun = runs.length ? runs[runs.length - 1] : null;

  // results are keyed by case id per run so each table row can look up its own cell.
  const resultsByRun = Object.fromEntries(
    runs.map((run) => [run.id, Object.fromEntries((run.results || []).map((r) => [r.case_id, r]))]),
  );

  const toggleRun = (runID) => {
    setHiddenRunIds((prev) => (prev.includes(runID) ? prev.filter((id) => id !== runID) : [...prev, runID]));
  };

  return (
    <div className="max-w-[1400px] mx-auto py-6">
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => navigate('/translation-lab')}
                className="p-1.5 rounded-md text-ink-dim hover:text-ink hover:bg-surface-2 transition-colors mt-0.5"
                aria-label="Back to sets"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div>
                <h2 className="text-xl font-bold">{set.name}</h2>
                <p className="text-xs text-ink-dim">
                  {cases.length} conversation{cases.length === 1 ? '' : 's'} · {runs.length} run{runs.length === 1 ? '' : 's'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button variant="outline" className="gap-1.5" onClick={() => setAddOpen(true)} disabled={running}>
                <Plus className="h-4 w-4" />Add conversations
              </Button>
              <RunControls agents={agents} lastRun={lastRun} running={running} onRun={handleRun} />
            </div>
          </div>

          {runs.length > 2 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-3 pt-3 border-t border-white/5">
              <span className="text-[11px] text-ink-mute mr-1">Compare:</span>
              {runs.map((run) => {
                const shown = !hiddenRunIds.includes(run.id);
                return (
                  <button key={run.id} type="button" onClick={() => toggleRun(run.id)}>
                    <Badge variant={shown ? 'default' : 'outline'} className="cursor-pointer">
                      {runLabel(run)} · {fmtDate(run.created_at)}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert variant="error" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          {runs.length === 0 ? (
            <p className="text-sm text-ink-dim text-center py-12">
              No runs yet. Pick a language pair or an agent above and run the set.
            </p>
          ) : null}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead className="min-w-[240px]">Source</TableHead>
                {visibleRuns.map((run) => (
                  <TableHead key={run.id} className="min-w-[260px]">
                    <div className="flex items-center gap-1.5">
                      <span className="text-ink font-bold">{runLabel(run)}</span>
                      <Badge variant={RUN_STATUS_VARIANT[run.status] || 'secondary'}>{run.status}</Badge>
                    </div>
                    <span className="text-[11px] text-ink-mute">
                      {run.source_language && run.target_language
                        ? `${run.source_language} → ${run.target_language} · `
                        : ''}
                      {fmtDate(run.created_at)}
                    </span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {cases.map((testCase, i) => (
                <TableRow key={testCase.id} className="align-top">
                  <TableCell className="text-ink-mute align-top">{i + 1}</TableCell>
                  <TableCell className="align-top">
                    <p className="text-sm text-ink">
                      {highlightSegments(
                        testCase.source_text,
                        // Protection is decided per translate call, so each run protected its own
                        // set of segments. The source column is shared, so it shows the union of
                        // what the visible runs protected — hidden runs contribute nothing.
                        unionSegments(visibleRuns.map((run) => resultsByRun[run.id]?.[testCase.id]?.protected_segments)),
                        'offsets',
                      )}
                    </p>
                    {testCase.history.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setHistoryCase(testCase)}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-ink-mute hover:text-ink transition-colors"
                      >
                        <MessagesSquare className="h-3 w-3" />
                        {testCase.history.length} context turn{testCase.history.length === 1 ? '' : 's'}
                      </button>
                    )}
                  </TableCell>

                  {visibleRuns.map((run) => {
                    const result = resultsByRun[run.id]?.[testCase.id];
                    if (!result) {
                      return <TableCell key={run.id} className="text-ink-mute align-top">—</TableCell>;
                    }
                    if (result.error) {
                      return (
                        <TableCell key={run.id} className="align-top">
                          <Badge variant="destructive">failed</Badge>
                          <p className="text-[11px] text-ink-mute mt-1 break-words">{result.error}</p>
                        </TableCell>
                      );
                    }
                    // A protected value absent from the translation means the model dropped its
                    // placeholder and bi-pepper's tolerant restore skipped it — a wallet address or
                    // promo code that never reached the reader. Worth a badge, not a buried diff.
                    const missing = missingSegmentValues(result.translated_text, result.protected_segments);
                    return (
                      <TableCell key={run.id} className="align-top">
                        <p className="text-sm text-ink">
                          {highlightSegments(result.translated_text, result.protected_segments, 'value')}
                        </p>
                        {(missing.length > 0 || result.detected_language) && (
                          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                            <DetectionNote result={result} />
                            {missing.length > 0 && (
                              <Badge variant="warning">
                                {missing.length} protected value{missing.length === 1 ? '' : 's'} dropped
                              </Badge>
                            )}
                          </div>
                        )}
                        <TranslationLabFeedbackControl result={result} onSaved={handleFeedbackSaved} />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <HistoryDialog testCase={historyCase} onOpenChange={(open) => { if (!open) setHistoryCase(null); }} />

      <AddCasesDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        setId={setId}
        existingCount={cases.length}
        onAdded={load}
      />
    </div>
  );
};

export default TranslationLabSetPage;
